/**
 * Pure, framework-free quality checks for the face-capture flow.
 *
 * Everything here works on data the MediaPipe FaceLandmarker already gives us
 * (normalized landmarks 0..1, and the 4x4 facial transformation matrix) plus
 * the raw <video> frame for brightness. No drawing, no React — just decisions.
 *
 * Every check returns either `null` (all good) or a string KEY that maps to an
 * `face.quality.*` i18n message, so the UI never hard-codes copy.
 */

/* -------------------------------------------------------------------------- */
/* Brightness                                                                 */
/* -------------------------------------------------------------------------- */

// A tiny offscreen canvas we reuse every frame to avoid reallocating.
let _lumaCanvas = null;
let _lumaCtx = null;

const DARK_BELOW = 60; // mean luma 0..255
const BRIGHT_ABOVE = 215;

/**
 * Estimate average brightness of the current video frame by down-sampling it
 * to a small canvas and averaging luma. Cheap enough to run every frame.
 *
 * @param {HTMLVideoElement} video
 * @returns {{ luma:number, status:('tooDark'|'tooBright'|null) }}
 */
export function checkBrightness(video) {
  if (!video || !video.videoWidth) return { luma: 128, status: null };

  if (!_lumaCanvas) {
    _lumaCanvas = document.createElement('canvas');
    _lumaCanvas.width = 32;
    _lumaCanvas.height = 32;
    _lumaCtx = _lumaCanvas.getContext('2d', { willReadFrequently: true });
  }

  try {
    _lumaCtx.drawImage(video, 0, 0, 32, 32);
    const { data } = _lumaCtx.getImageData(0, 0, 32, 32);
    let sum = 0;
    const pixels = data.length / 4;
    for (let i = 0; i < data.length; i += 4) {
      // Rec. 601 luma.
      sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    const luma = sum / pixels;
    let status = null;
    if (luma < DARK_BELOW) status = 'tooDark';
    else if (luma > BRIGHT_ABOVE) status = 'tooBright';
    return { luma, status };
  } catch {
    // Cross-origin / not-ready frames can throw on getImageData — treat as OK.
    return { luma: 128, status: null };
  }
}

/* -------------------------------------------------------------------------- */
/* Face position                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Bounding box of a face's normalized landmarks.
 * @param {Array<{x:number,y:number,z:number}>} landmarks
 * @returns {{minX:number,minY:number,maxX:number,maxY:number,cx:number,cy:number,w:number,h:number}|null}
 */
export function faceBox(landmarks) {
  if (!landmarks || landmarks.length === 0) return null;
  let minX = 1;
  let minY = 1;
  let maxX = 0;
  let maxY = 0;
  for (const p of landmarks) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return {
    minX, minY, maxX, maxY,
    cx: (minX + maxX) / 2,
    cy: (minY + maxY) / 2,
    w: maxX - minX,
    h: maxY - minY,
  };
}

const FACE_TOO_SMALL = 0.35; // face height as fraction of frame
const FACE_TOO_LARGE = 0.85;
const OFFSET_X = 0.15;
const OFFSET_Y = 0.18;

/**
 * Decide whether a face is well framed.
 * @param {ReturnType<typeof faceBox>} box
 * @returns {('noFace'|'tooFar'|'tooClose'|'offCenter'|null)}
 */
export function checkPosition(box) {
  if (!box) return 'noFace';
  if (box.h < FACE_TOO_SMALL) return 'tooFar';
  if (box.h > FACE_TOO_LARGE) return 'tooClose';
  if (Math.abs(box.cx - 0.5) > OFFSET_X) return 'offCenter';
  if (Math.abs(box.cy - 0.5) > OFFSET_Y) return 'offCenter';
  return null;
}

/* -------------------------------------------------------------------------- */
/* Head pose (is the client looking straight at the camera?)                  */
/* -------------------------------------------------------------------------- */

const RAD2DEG = 180 / Math.PI;

/**
 * Extract yaw/pitch/roll (degrees) from the FaceLandmarker facial
 * transformation matrix. MediaPipe returns the 4x4 matrix in column-major
 * order as a flat 16-element array.
 *
 * Column-major flat index = col * 4 + row, so the rotation sub-matrix is:
 *   r00 = m[0]  r01 = m[4]  r02 = m[8]
 *   r10 = m[1]  r11 = m[5]  r12 = m[9]
 *   r20 = m[2]  r21 = m[6]  r22 = m[10]
 *
 * @param {number[]} m flat length-16 column-major matrix
 * @returns {{yaw:number,pitch:number,roll:number}}
 */
export function headPoseFromMatrix(m) {
  if (!m || m.length < 11) return { yaw: 0, pitch: 0, roll: 0 };
  const r00 = m[0];
  const r10 = m[1];
  const r20 = m[2];
  const r21 = m[6];
  const r22 = m[10];

  const pitch = Math.atan2(r21, r22) * RAD2DEG;
  const yaw = Math.atan2(-r20, Math.sqrt(r21 * r21 + r22 * r22)) * RAD2DEG;
  const roll = Math.atan2(r10, r00) * RAD2DEG;
  return { yaw, pitch, roll };
}

const YAW_LIMIT = 18; // degrees off-axis we still accept as "frontal"
const PITCH_LIMIT = 16;
const ROLL_LIMIT = 12;

/**
 * Decide whether the head is sufficiently frontal & upright.
 * @param {{yaw:number,pitch:number,roll:number}} pose
 * @returns {('notFrontal'|'tilted'|null)}
 */
export function checkFrontal(pose) {
  if (!pose) return null;
  if (Math.abs(pose.yaw) > YAW_LIMIT || Math.abs(pose.pitch) > PITCH_LIMIT) return 'notFrontal';
  if (Math.abs(pose.roll) > ROLL_LIMIT) return 'tilted';
  return null;
}

/* -------------------------------------------------------------------------- */
/* Aggregate                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Run every framing/lighting check and return the single most important
 * problem (or null when the frame is good to capture). Order matters: we tell
 * the operator about a missing/badly-placed face before nit-picking lighting.
 *
 * @param {object} args
 * @param {HTMLVideoElement} args.video
 * @param {Array} args.landmarks  one face's normalized landmarks (or empty)
 * @param {number} args.faceCount how many faces were detected
 * @param {number[]} args.matrix  facial transformation matrix (flat 16)
 * @param {boolean} [args.requireFrontal=true] enforce the frontal/upright gate.
 *        Set false for intentional head-angle poses (laterals, up/down) so a
 *        turned/tilted head isn't rejected — presence, framing and lighting
 *        still apply.
 * @returns {{ status:string|null, headPose:{yaw:number,pitch:number,roll:number}, box:object|null, luma:number }}
 */
export function evaluateFrame({ video, landmarks, faceCount, matrix, requireFrontal = true }) {
  const box = faceBox(landmarks);
  const headPose = headPoseFromMatrix(matrix);
  const { luma, status: brightness } = checkBrightness(video);

  let status = null;
  if (faceCount > 1) status = 'multipleFaces';
  else {
    status = checkPosition(box);
    if (!status && requireFrontal) status = checkFrontal(headPose);
    if (!status) status = brightness; // lighting last
  }

  return { status, headPose, box, luma };
}
